import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Plus, X } from "lucide-react";
import moment from "moment";
import { getAdminEventById, createEvent, updateEvent, getAllTournamentsForSelect, getAllSeasonsForSelect } from "../../../api/events";
import { uploadEventLogo, uploadEventCover, uploadEventSponsorLogo } from "../../../services/firebase";
import { CreateEventDTO, EventSponsorInputDTO } from "../../../types/event";
import { IEventDetail } from "../../../models/IEvent";
import { useAppDispatch } from "../../../store/store";
import { setGeneralAlertMessage } from "../../../store/slices/generalSlice";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ColorInput,
  DatePicker,
  Field,
  FileButton,
  FormGrid,
  IconButton,
  Input,
  Loader,
  PageHeader,
  SearchInput,
  Textarea,
  UploadBox,
} from "../../../ui";
import "./styles.scss";

interface SponsorRow extends EventSponsorInputDTO {
  _localId: string;
  _localFile: File | null;
}

interface TournamentOption { id: string; name: string; type: string; }
interface SeasonOption { id: string; name: string; typeName: string; }

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const EventEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [existingEvent, setExistingEvent] = useState<IEventDetail | null>(null);

  // Branding
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>("");
  const [primaryColor, setPrimaryColor] = useState("#fcc353");
  const [secondaryColor, setSecondaryColor] = useState("#020618");
  const [accentColor, setAccentColor] = useState("#ffffff");

  // Details
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");

  // Assignments
  const [allTournaments, setAllTournaments] = useState<TournamentOption[]>([]);
  const [allSeasons, setAllSeasons] = useState<SeasonOption[]>([]);
  const [selectedTournamentIds, setSelectedTournamentIds] = useState<string[]>([]);
  const [selectedSeasonIds, setSelectedSeasonIds] = useState<string[]>([]);
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [seasonSearch, setSeasonSearch] = useState("");

  // Sponsors
  const [sponsors, setSponsors] = useState<SponsorRow[]>([]);

  // Social
  const [instagramUrl, setInstagramUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // Visibility
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [visibleFrom, setVisibleFrom] = useState<Date | null>(null);
  const [visibleTo, setVisibleTo] = useState<Date | null>(null);

  const loadSelectionLists = useCallback(async () => {
    try {
      // Both helpers already return flat arrays ({id,name,type} / {id,name,typeName}).
      const [tournaments, seasons] = await Promise.all([getAllTournamentsForSelect(), getAllSeasonsForSelect()]);
      setAllTournaments(tournaments);
      setAllSeasons(seasons);
    } catch {
      // Non-blocking: selection lists may be empty
    }
  }, []);

  const populateForm = useCallback((event: IEventDetail) => {
    setExistingEvent(event);
    setName(event.name);
    setSlug(event.slug);
    setSlugManuallyEdited(true); // Don't auto-overwrite on edit
    setDescription(event.description || "");
    setPrimaryColor(event.primaryColor || "#fcc353");
    setSecondaryColor(event.secondaryColor || "#020618");
    setAccentColor(event.accentColor || "#ffffff");
    setLogoPreview(event.logoUrl || "");
    setCoverPreview(event.coverUrl || "");
    setInstagramUrl(event.instagramUrl || "");
    setFacebookUrl(event.facebookUrl || "");
    setWebsiteUrl(event.websiteUrl || "");
    setTiktokUrl(event.tiktokUrl || "");
    setYoutubeUrl(event.youtubeUrl || "");
    setContactEmail(event.contactEmail || "");
    setIsActive(event.isActive);
    setDisplayOrder(event.displayOrder);
    setVisibleFrom(event.visibleFrom ? new Date(event.visibleFrom) : null);
    setVisibleTo(event.visibleTo ? new Date(event.visibleTo) : null);
    setSelectedTournamentIds(event.tournamentIds || []);
    setSelectedSeasonIds(event.seasonIds || []);
    setSponsors(
      (event.sponsors || []).map((s) => ({
        _localId: `${s.id}`,
        _localFile: null,
        name: s.name,
        logoUrl: s.logoUrl,
        linkUrl: s.linkUrl,
        displayOrder: s.displayOrder,
      }))
    );
  }, []);

  useEffect(() => {
    loadSelectionLists();
    if (isEdit && id) {
      setLoading(true);
      getAdminEventById(id)
        .then(({ data }) => populateForm(data))
        .catch((err: any) =>
          dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load event", type: "error" }))
        )
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, loadSelectionLists, populateForm, dispatch]);

  // Auto-generate slug from name (only when not manually edited)
  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setSlugManuallyEdited(true);
  };

  // Image handlers
  const handleLogoChange = (file: File) => {
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleCoverChange = (file: File) => {
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  // Sponsor helpers
  const addSponsor = () => {
    const localId = `new-${Date.now()}`;
    setSponsors((prev) => [
      ...prev,
      { _localId: localId, _localFile: null, name: "", logoUrl: null, linkUrl: null, displayOrder: prev.length },
    ]);
  };

  const removeSponsor = (localId: string) => {
    setSponsors((prev) => prev.filter((s) => s._localId !== localId));
  };

  const updateSponsorField = (localId: string, field: keyof EventSponsorInputDTO, value: any) => {
    setSponsors((prev) =>
      prev.map((s) => (s._localId === localId ? { ...s, [field]: value } : s))
    );
  };

  const handleSponsorLogoChange = (localId: string, files: FileList) => {
    const file = files[0];
    if (!file) return;
    setSponsors((prev) =>
      prev.map((s) =>
        s._localId === localId
          ? { ...s, _localFile: file, logoUrl: URL.createObjectURL(file) }
          : s
      )
    );
  };

  // Toggle selection helpers
  const toggleTournament = (tid: string) => {
    setSelectedTournamentIds((prev) =>
      prev.includes(tid) ? prev.filter((x) => x !== tid) : [...prev, tid]
    );
  };

  const toggleSeason = (sid: string) => {
    setSelectedSeasonIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const handleSubmit = async () => {
    setErrorMessage("");

    if (!name.trim()) { setErrorMessage("Event name is required"); return; }
    if (!slug.trim()) { setErrorMessage("Slug is required"); return; }
    if (visibleFrom && visibleTo && visibleFrom > visibleTo) {
      setErrorMessage("Visible From must be before Visible To");
      return;
    }

    setSaving(true);
    try {
      // Determine the event id for uploads (use existing id when editing, or a temp key we'll replace post-create)
      let resolvedId = id || "";

      // When creating, we need to call the API first to get the id, then re-upload if files were picked
      // Strategy: create with existing URLs if present, then re-PUT with Firebase URLs

      let logoUrl: string | null = existingEvent?.logoUrl || null;
      let coverUrl: string | null = existingEvent?.coverUrl || null;

      // If editing and new files selected, upload now (we have the id)
      if (isEdit && id) {
        if (logoFile) logoUrl = await uploadEventLogo(id, logoFile);
        if (coverFile) coverUrl = await uploadEventCover(id, coverFile);
      }

      // Upload sponsor logos that have new local files (edit mode only; create is done post-create)
      const sponsorDTOs: EventSponsorInputDTO[] = await Promise.all(
        sponsors.map(async (s, index) => {
          let sLogoUrl = s.logoUrl && !s.logoUrl.startsWith("blob:") ? s.logoUrl : null;
          if (s._localFile && isEdit && id) {
            sLogoUrl = await uploadEventSponsorLogo(id, index, s._localFile);
          }
          return {
            name: s.name,
            logoUrl: sLogoUrl,
            linkUrl: s.linkUrl || null,
            displayOrder: s.displayOrder,
          };
        })
      );

      const dto: CreateEventDTO = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        logoUrl,
        coverUrl,
        primaryColor: primaryColor || null,
        secondaryColor: secondaryColor || null,
        accentColor: accentColor || null,
        instagramUrl: instagramUrl.trim() || null,
        facebookUrl: facebookUrl.trim() || null,
        websiteUrl: websiteUrl.trim() || null,
        tiktokUrl: tiktokUrl.trim() || null,
        youtubeUrl: youtubeUrl.trim() || null,
        contactEmail: contactEmail.trim() || null,
        isActive,
        visibleFrom: visibleFrom ? moment(visibleFrom).utc().format("YYYY-MM-DDTHH:mm:ss[Z]") : null,
        visibleTo: visibleTo ? moment(visibleTo).utc().format("YYYY-MM-DDTHH:mm:ss[Z]") : null,
        displayOrder,
        tournamentIds: selectedTournamentIds,
        seasonIds: selectedSeasonIds,
        sponsors: sponsorDTOs,
      };

      if (isEdit && id) {
        await updateEvent(id, dto);
        dispatch(setGeneralAlertMessage({ message: "Event updated successfully", type: "success" }));
        navigate("/dashboard/events");
      } else {
        // Create first to get the id
        const { data: created } = await createEvent(dto);
        resolvedId = created.id;

        // Now upload branding files with the real id and re-save
        let needsResave = false;
        let newLogoUrl = logoUrl;
        let newCoverUrl = coverUrl;

        if (logoFile) {
          newLogoUrl = await uploadEventLogo(resolvedId, logoFile);
          needsResave = true;
        }
        if (coverFile) {
          newCoverUrl = await uploadEventCover(resolvedId, coverFile);
          needsResave = true;
        }

        const updatedSponsorDTOs: EventSponsorInputDTO[] = await Promise.all(
          sponsors.map(async (s, index) => {
            let sLogoUrl = s.logoUrl && !s.logoUrl.startsWith("blob:") ? s.logoUrl : null;
            if (s._localFile) {
              sLogoUrl = await uploadEventSponsorLogo(resolvedId, index, s._localFile);
              needsResave = true;
            }
            return {
              name: s.name,
              logoUrl: sLogoUrl,
              linkUrl: s.linkUrl || null,
              displayOrder: s.displayOrder,
            };
          })
        );

        if (needsResave) {
          await updateEvent(resolvedId, {
            ...dto,
            logoUrl: newLogoUrl,
            coverUrl: newCoverUrl,
            sponsors: updatedSponsorDTOs,
          });
        }

        dispatch(setGeneralAlertMessage({ message: "Event created successfully", type: "success" }));
        navigate("/dashboard/events");
      }
    } catch (err: any) {
      setErrorMessage(err?.message || err?.errors?.[0]?.description || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const filteredTournaments = allTournaments.filter((t) =>
    !tournamentSearch || t.name.toLowerCase().includes(tournamentSearch.toLowerCase())
  );

  const filteredSeasons = allSeasons.filter((s) =>
    !seasonSearch || s.name.toLowerCase().includes(seasonSearch.toLowerCase())
  );

  if (loading) {
    return <Loader className="event-editor-loading" />;
  }

  // The event's own colours: the kit picker (swatches + hex). Only full #rrggbb values are taken, as before.
  const colorField = (label: string, value: string, setValue: (v: string) => void) => (
    <Field label={label}>
      {(fieldId) => (
        <ColorInput
          id={fieldId}
          aria-label={label}
          value={value}
          onChange={(e) => {
            if (HEX_COLOR_RE.test(e.target.value)) setValue(e.target.value);
          }}
        />
      )}
    </Field>
  );

  return (
    <div className="event-editor">
      <PageHeader
        title={isEdit ? "Edit Event" : "Create Event"}
        back={{ onClick: () => navigate("/dashboard/events"), label: "Back to Events" }}
      />

      {errorMessage && <Alert tone="danger" className="editor-error">{errorMessage}</Alert>}

      {/* ── Section 1: Branding ── */}
      <Card
        className="editor-section"
        title="Branding"
        subtitle="Upload a logo and cover image, and pick the colors used on the public event page."
      >
        <div className="upload-row">
          {/* Logo */}
          <Field label="Logo" className="upload-field logo-field">
            <UploadBox
              aspect="square"
              previewUrl={logoPreview || null}
              onPick={handleLogoChange}
              label="Upload logo"
            />
          </Field>

          {/* Cover */}
          <Field label="Cover image" className="upload-field cover-field">
            <UploadBox
              previewUrl={coverPreview || null}
              onPick={handleCoverChange}
              label="Upload cover"
            />
          </Field>
        </div>

        <div className="color-row">
          {colorField("Primary color", primaryColor, setPrimaryColor)}
          {colorField("Secondary color", secondaryColor, setSecondaryColor)}
          {colorField("Accent color (optional)", accentColor, setAccentColor)}
        </div>
      </Card>

      {/* ── Section 2: Details ── */}
      <Card
        className="editor-section"
        title="Details"
        subtitle="The event name and slug identify this event across the platform and in public URLs."
      >
        <div className="editor-stack">
          <Field label="Event name" required>
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., The Clash Beach Open 2026"
                disabled={saving}
              />
            )}
          </Field>

          <Field
            label="Slug"
            required
            hint={<>Used in URL: /events/<strong>{slug || "..."}</strong></>}
          >
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="beach-open-2026"
                disabled={saving}
              />
            )}
          </Field>

          <Field label="Description">
            {(fieldId) => (
              <Textarea
                id={fieldId}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this event for public-facing pages..."
                rows={5}
                disabled={saving}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* ── Section 3: Assignments ── */}
      <Card
        className="editor-section"
        title="Assignments"
        subtitle="Choose which tournaments and seasons belong to this event. They will appear grouped on the event page."
      >
        <div className="assignment-columns">
          {/* Tournaments */}
          <Field label={`Tournaments (${selectedTournamentIds.length} selected)`} className="assignment-col">
            {(fieldId) => (
              <>
                <SearchInput
                  id={fieldId}
                  block
                  placeholder="Search tournaments..."
                  value={tournamentSearch}
                  onChange={setTournamentSearch}
                />
                <div className="assignment-list">
                  {filteredTournaments.map((t) => (
                    <Checkbox
                      key={t.id}
                      className="assignment-item"
                      checked={selectedTournamentIds.includes(t.id)}
                      onChange={() => toggleTournament(t.id)}
                      label={
                        <span className="assignment-item__text">
                          <span>{t.name}</span>
                          <span className="item-meta">{t.type}</span>
                        </span>
                      }
                    />
                  ))}
                  {filteredTournaments.length === 0 && (
                    <p className="empty-msg">No tournaments found</p>
                  )}
                </div>
              </>
            )}
          </Field>

          {/* Seasons */}
          <Field label={`Seasons (${selectedSeasonIds.length} selected)`} className="assignment-col">
            {(fieldId) => (
              <>
                <SearchInput
                  id={fieldId}
                  block
                  placeholder="Search seasons..."
                  value={seasonSearch}
                  onChange={setSeasonSearch}
                />
                <div className="assignment-list">
                  {filteredSeasons.map((s) => (
                    <Checkbox
                      key={s.id}
                      className="assignment-item"
                      checked={selectedSeasonIds.includes(s.id)}
                      onChange={() => toggleSeason(s.id)}
                      label={
                        <span className="assignment-item__text">
                          <span>{s.name}</span>
                          <span className="item-meta">{s.typeName}</span>
                        </span>
                      }
                    />
                  ))}
                  {filteredSeasons.length === 0 && (
                    <p className="empty-msg">No seasons found</p>
                  )}
                </div>
              </>
            )}
          </Field>
        </div>
      </Card>

      {/* ── Section 4: Sponsors ── */}
      <Card
        className="editor-section"
        title="Sponsors"
        subtitle="Add sponsor logos and links that appear on the public event page."
      >
        {sponsors.map((sponsor) => (
          <div key={sponsor._localId} className="sponsor-row">
            {/* The logo as it will show, and a kit file button under it (the full UploadBox is too tall for the row) */}
            <div className="sponsor-logo-upload">
              <div className="sponsor-logo-thumb">
                {sponsor.logoUrl ? (
                  <img src={sponsor.logoUrl} alt={sponsor.name} className="sponsor-logo-preview" />
                ) : (
                  <Upload size={16} />
                )}
              </div>
              <FileButton
                variant="tint"
                size="sm"
                className="sponsor-logo-btn"
                icon={<Upload size={14} />}
                accept="image/*"
                aria-label="Upload sponsor logo"
                data-tooltip="Upload sponsor logo"
                onFiles={(files) => handleSponsorLogoChange(sponsor._localId, files)}
              />
            </div>

            <div className="sponsor-fields">
              <Input
                type="text"
                placeholder="Sponsor name *"
                aria-label="Sponsor name"
                value={sponsor.name}
                onChange={(e) => updateSponsorField(sponsor._localId, "name", e.target.value)}
                disabled={saving}
              />
              <Input
                type="text"
                placeholder="Link URL (optional)"
                aria-label="Link URL (optional)"
                value={sponsor.linkUrl || ""}
                onChange={(e) => updateSponsorField(sponsor._localId, "linkUrl", e.target.value || null)}
                disabled={saving}
              />
            </div>

            <Field label="Order" className="sponsor-order">
              {(fieldId) => (
                <Input
                  id={fieldId}
                  type="number"
                  block={false}
                  value={sponsor.displayOrder}
                  onChange={(e) => updateSponsorField(sponsor._localId, "displayOrder", Number(e.target.value))}
                  disabled={saving}
                  style={{ width: 72 }}
                />
              )}
            </Field>

            <IconButton
              className="remove-sponsor-btn"
              label="Remove sponsor"
              icon={<X size={16} />}
              variant="danger"
              onClick={() => removeSponsor(sponsor._localId)}
              disabled={saving}
            />
          </div>
        ))}

        <Button variant="tint" size="sm" icon={<Plus size={14} />} onClick={addSponsor} disabled={saving}>
          Add sponsor
        </Button>
      </Card>

      {/* ── Section 5: Social ── */}
      <Card
        className="editor-section"
        title="Social & contact"
        subtitle="Links and contact details shown on the public event page. All fields are optional."
      >
        <FormGrid columns={2}>
          <Field label="Instagram URL">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="https://instagram.com/..."
                disabled={saving}
              />
            )}
          </Field>
          <Field label="Facebook URL">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                placeholder="https://facebook.com/..."
                disabled={saving}
              />
            )}
          </Field>
          <Field label="Website URL">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://..."
                disabled={saving}
              />
            )}
          </Field>
          <Field label="TikTok URL">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                placeholder="https://tiktok.com/..."
                disabled={saving}
              />
            )}
          </Field>
          <Field label="YouTube URL">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://youtube.com/..."
                disabled={saving}
              />
            )}
          </Field>
          <Field label="Contact email">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@..."
                disabled={saving}
              />
            )}
          </Field>
        </FormGrid>
      </Card>

      {/* ── Section 6: Visibility ── */}
      <Card
        className="editor-section"
        title="Visibility"
        subtitle="Control when and whether this event appears to users. Use the date window to schedule a timed reveal."
      >
        <div className="editor-stack">
          <FormGrid columns={2}>
            <Field label="Display order">
              {(fieldId) => (
                <Input
                  id={fieldId}
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(Number(e.target.value))}
                  disabled={saving}
                />
              )}
            </Field>
          </FormGrid>

          <Checkbox
            checked={isActive}
            onChange={setIsActive}
            disabled={saving}
            label="Active (required for public visibility)"
          />

          <FormGrid columns={2}>
            <Field label="Visible from (optional)">
              {(fieldId) => (
                <DatePicker
                  id={fieldId}
                  block
                  mode="datetime"
                  selected={visibleFrom}
                  onChange={(date: Date | null) => setVisibleFrom(date)}
                  dateFormat="dd MMM yyyy HH:mm"
                  placeholderText="No start restriction"
                  disabled={saving}
                  maxDate={visibleTo || undefined}
                  isClearable
                />
              )}
            </Field>
            <Field label="Visible to (optional)">
              {(fieldId) => (
                <DatePicker
                  id={fieldId}
                  block
                  mode="datetime"
                  selected={visibleTo}
                  onChange={(date: Date | null) => setVisibleTo(date)}
                  dateFormat="dd MMM yyyy HH:mm"
                  placeholderText="No end restriction"
                  disabled={saving}
                  minDate={visibleFrom || undefined}
                  isClearable
                />
              )}
            </Field>
          </FormGrid>
        </div>
      </Card>

      <div className="editor-footer">
        <Button variant="ghost" onClick={() => navigate("/dashboard/events")} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={saving}>
          {isEdit ? "Save event" : "Create event"}
        </Button>
      </div>
    </div>
  );
};

export default EventEditor;
